import { useState } from "react";
import { Link } from "react-router-dom";
import { api, TYPE_META, TYPE_ORDER } from "@/lib/api";
import { Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

/** Post-import sortable list. Each row is the freshly-created item with a
 *  shelf-selector and a delete affordance. Used by both Settings (bulk import)
 *  and Add (single-link list expansion). */
export default function ImportedList({ items, onChange, onRemove, title = "Just imported · sort & file" }) {
  const [list, setList] = useState(items);

  async function reclassify(item, newType) {
    try {
      const r = await api.patch(`/items/${item.id}`, { type: newType });
      const updated = r.data;
      setList(prev => prev.map(i => i.id === item.id ? updated : i));
      if (onChange) onChange(updated);
      toast.success("Re-shelved");
    } catch { toast.error("Could not re-shelve"); }
  }
  async function remove(item) {
    try {
      await api.delete(`/items/${item.id}`);
      setList(prev => prev.filter(i => i.id !== item.id));
      if (onRemove) onRemove(item.id);
      toast.success("Removed");
    } catch { toast.error("Could not remove"); }
  }
  return (
    <div className="placed-card overflow-hidden" data-testid="imported-list">
      <header className="px-4 py-3 border-b border-[rgba(140,106,47,0.18)] flex items-center justify-between">
        <p className="font-cinzel text-[10px] tracking-[0.26em] uppercase text-[var(--bronze)]">
          {title}
        </p>
        <p className="font-mono-lc text-[10px] tracking-[0.16em] text-[var(--ink-light)]">{list.length} items</p>
      </header>
      <ul className="divide-y divide-[rgba(140,106,47,0.14)] max-h-[420px] overflow-y-auto">
        {list.map(it => {
          const meta = TYPE_META[it.type] || TYPE_META.unknown;
          return (
            <li key={it.id} className="flex items-center gap-3 p-3" data-testid={`imported-${it.id}`}>
              <div className="w-10 h-10 shrink-0 rounded overflow-hidden" style={{ background: meta.bg }}>
                {it.image_url && <img src={it.image_url} alt="" className="w-full h-full object-cover"/>}
              </div>
              <div className="flex-1 min-w-0">
                <Link to={`/item/${it.id}`} className="font-serif-lit text-sm text-[var(--ink)] line-clamp-1 hover:underline">
                  {it.title || "Untitled"}
                </Link>
                {it.creator && <p className="font-garamond italic text-xs text-[var(--ink-dusty)] line-clamp-1">{it.creator}</p>}
              </div>
              <select
                value={it.type}
                onChange={(e) => reclassify(it, e.target.value)}
                data-testid={`imported-type-${it.id}`}
                className="bg-transparent border border-[rgba(42,30,20,0.18)] rounded px-2 py-1 font-cinzel text-[10px] tracking-[0.18em] uppercase"
                style={{ color: meta.accent }}
              >
                {TYPE_ORDER.map(t => <option key={t} value={t}>{TYPE_META[t].label}</option>)}
              </select>
              {it.source_url && (
                <a href={it.source_url} target="_blank" rel="noreferrer" className="text-[var(--ink-dusty)] hover:text-[var(--bronze)]" title="Open source">
                  <ExternalLink size={13}/>
                </a>
              )}
              <button onClick={() => remove(it)} className="text-[var(--ink-dusty)] hover:text-[var(--terracotta)]" data-testid={`imported-delete-${it.id}`} title="Remove">
                <Trash2 size={13}/>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
